import * as CryptoJS from "crypto-js";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { deviceDetect, isDesktop } from "react-device-detect";
import { detectCountry } from "./utility";

/**
 * Encrypts authentication data by adding trxTime and trxHash
 * @param {Object} data - Authentication data object
 * @returns {Object} - Data with trxTime and trxHash added
 */
export const encryptionAuth = (data) => {
  data.trxTime = moment().unix();
  let toHashStr = "";
  let hashStr = "";
  let paramsKey = [];

  // Collect all keys from data
  for (const key in data) {
    if (!paramsKey.includes(key)) {
      paramsKey.push(key);
    }
  }

  // Sort keys alphabetically
  paramsKey.sort();

  // Build hash string from sorted keys
  paramsKey.forEach((val) => {
    let x = data[val];
    if (x !== "") {
      if (val === "file") {
        // file required hashing params
        toHashStr += val + x.name + x.size;
      } else {
        toHashStr += val + x;
      }
    }
  });

  // Add secret and generate MD5 hash
  toHashStr += process.env.REACT_APP_HASH_SECRET;
  hashStr = CryptoJS.MD5(toHashStr).toString();
  data.trxHash = hashStr;

  return data;
};

/**
 * Handles successful login by updating user data, collecting fingerprint, and navigating
 * @param {Object} params - Configuration object
 * @param {Object} params.loginData - Response data from login API
 * @param {Object} params.currentUser - Current user data from Redux state
 * @param {Function} params.getUserDataAction - Redux action to update user data
 * @param {Function} params.updateFingerPrintAction - Redux action to update fingerprint (optional if shouldCollectFingerprint is false)
 * @param {Object} params.history - React Router history object
 * @param {Object} params.i18n - i18n instance for language change
 * @param {Object} [params.options={}] - Optional configurations
 * @param {boolean} [params.options.shouldCollectFingerprint=true] - Whether to collect fingerprint
 * @param {boolean} [params.options.shouldPreserveLanguage=true] - Whether to preserve language on same country
 * @param {Function} [params.options.getTopProfileAction=null] - Optional action to call getTopProfile
 * @param {boolean} [params.options.forceReload=false] - Whether to force page reload after navigation
 * @param {string|null} [params.options.redirectUrl=null] - Custom redirect URL
 * @param {boolean} [params.options.showBackupUrlModal=false] - Whether to show backup URL modal on desktop
 * @returns {Promise<void>}
 */
export const onLoginSuccessCallback = async ({
  loginData,
  currentUser,
  getUserDataAction,
  updateFingerPrintAction = null,
  history,
  i18n,
  options = {}
}) => {
  // Default options
  const {
    shouldPreserveLanguage = true,
    getTopProfileAction = null,
    forceReload = false,
    redirectUrl = null,
    showBackupUrlModal = false
  } = options;

  try {
    // Close any active Swal dialogs
    Swal.close();

    // Session cleanup
    sessionStorage.removeItem("telegram_user_info");
    sessionStorage.removeItem("google_user_info");

    // Build logged user data
    const loggedData = {};
    loggedData.id = loginData.id;
    loggedData.username = loginData.username;
    loggedData.name = loginData.name;
    loggedData.user_group = loginData.user_group;
    loggedData.user_group_level = loginData.user_group_level;
    loggedData.country_id = loginData.country_id;
    loggedData.country_code = loginData.country_code;
    loggedData.currency = loginData.currency;

    // Language handling based on country change and preservation flag
    if (shouldPreserveLanguage && currentUser.country_id !== loginData.country_id) {
      // Country changed: use new language
      loggedData.language_id = loginData.language_id;
      loggedData.language_code = loginData.language_code;
    } else if (shouldPreserveLanguage) {
      // Country same: preserve current language
      loggedData.language_id = currentUser.language_id;
      loggedData.language_code = currentUser.language_code;
    } else {
      // Don't preserve: always use new language
      loggedData.language_id = loginData.language_id;
      loggedData.language_code = loginData.language_code;
    }

    loggedData.member_group_id = loginData.group_id;
    loggedData.group_id = loginData.group_id;
    loggedData.contact_no = loginData.contact_no;
    loggedData.email = loginData.email;
    loggedData.balance = loginData.balance;
    loggedData.register_date_time = loginData.created_at;
    loggedData.time_zone = loginData.time_zone;

    // Update user data in Redux store
    getUserDataAction(loggedData);

    // Store access token
    localStorage.setItem("accessToken", loginData.access_token);

    // Handle language change
    if (shouldPreserveLanguage && currentUser.country_id !== loginData.country_id) {
      const defaultLang =
        loginData.language_code
          .replace("-", "_")
          .toString()
          .toLowerCase() || "en";
      localStorage.setItem("lang", defaultLang);
      i18n.changeLanguage(defaultLang); // force language change
    } else if (shouldPreserveLanguage) {
      localStorage.setItem(
        "lang",
        currentUser.language_code
          .replace("-", "_")
          .toString()
          .toLowerCase() || "en"
      );
    } else {
      const lang =
        loginData.language_code
          .replace("-", "_")
          .toString()
          .toLowerCase() || "en";
      localStorage.setItem("lang", lang);
    }

    // Reset VIP details if country changed
    localStorage.removeItem('vipDetails');

    // Optional: Call getTopProfile action
    if (getTopProfileAction) {
      getTopProfileAction();
    }

    const fingerPrintData = {};

    const ipv4Promise = axios
      .get('https://api4.my-ip.io/v2/ip.json')
      .then((response) => {
        if (response.data) {
          fingerPrintData.ip_address = response.data.ip;
        }
      }).catch((error) => {
        console.error(error);
      });

    const ipv6Promise = axios
      .get('https://api6.my-ip.io/v2/ip.json')
      .then((response) => {
        if (response.data) {
          fingerPrintData.ip_address_v6 = response.data.ip;
        }
      }).catch((error) => {
        console.error(error);
      });

    const fingerPrintPromise = FingerprintJS.load()
        .then((fp) => fp.get())
        .then(async (result) => {
          fingerPrintData.member_id = loginData.id;
          fingerPrintData.browser = result.components.vendorFlavors.value[0];
          fingerPrintData.browser_version = result.browserVersion;
          fingerPrintData.operating_system = result.components.platform.value;
          fingerPrintData.operating_system_version = result.osVersion;
          fingerPrintData.finger_print_id = result.visitorId;

          // Get device model for mobile devices
          if (!isDesktop) {
            const deviceModel = deviceDetect();
            fingerPrintData.device_model = deviceModel.vendor;
            fingerPrintData.device_model_code = deviceModel.model;
          }
        }).catch((error) => {
          console.error('Fingerprint collection failed:', error);
        });

    await Promise.all([ ipv4Promise, ipv6Promise, fingerPrintPromise ]);
    
    // Update fingerprint
    updateFingerPrintAction(fingerPrintData);

    // Navigation logic
    const dynamicCountry = detectCountry(window.location.host);

    if (redirectUrl) {
      localStorage.removeItem("redirectAfterLogin");
      window.location.replace(redirectUrl);
    }

    if (isDesktop) {
      if (!redirectUrl) {
        if (showBackupUrlModal) {
          history.push({
            pathname: "/",
            state: { showBackupUrlModal: true },
          });
        } else {
          history.push("/");
        }

        if (forceReload) {
          window.location.reload();
        }
      }
    } else {
      if (!redirectUrl) {
        history.push(`/online-live-games-${dynamicCountry.label}`);
        if (forceReload) {
          window.location.reload();
        }
      }
    }
  } catch (error) {
    console.error('Error in onLoginSuccessCallback:', error);
    throw error;
  }
};